<p-toast position="top-right"></p-toast>

<div class="bc-panel fadein">

  <!-- ══ Panel Header ══════════════════════════════════════════ -->
  <div class="panel-header">
    <div class="panel-header__left">
      <div class="panel-header__icon">
        <i class="pi pi-calendar"></i>
      </div>
      <div>
        <h2 class="panel-title">Appointment & Booking</h2>
        <p class="panel-sub">Configure booking rules, working schedules, and blocked dates for your company.</p>
      </div>
    </div>
    <div class="bc-badge">
      <i class="pi pi-shield"></i> Company-wide
    </div>
  </div>

  <!-- ══ Inner Tabs ════════════════════════════════════════════ -->
  <div class="inner-tabs">
    <button class="inner-tab" [class.active]="bookingTab === 'general'" (click)="switchTab('general')">
      <i class="pi pi-sliders-h"></i> General Config
    </button>
    <button class="inner-tab" [class.active]="bookingTab === 'hours'" (click)="switchTab('hours')">
      <i class="pi pi-clock"></i> Working Hours
    </button>
    <button class="inner-tab" [class.active]="bookingTab === 'blocked'" (click)="switchTab('blocked')">
      <i class="pi pi-ban"></i> Blocked Dates
    </button>
  </div>

  <!-- ══════════════ GENERAL CONFIG TAB ══════════════════════ -->
  <div *ngIf="bookingTab === 'general'" class="bc-tab-body">

    <div *ngIf="loadingConfig" class="settings-loading">
      <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
      <span>Loading configuration…</span>
    </div>

    <!-- Section: Timezone -->
    <div class="bc-section">
      <div class="bc-section__header">
        <div class="bc-section__icon bc-icon--blue">
          <i class="pi pi-globe"></i>
        </div>
        <div>
          <div class="bc-section__title">Timezone</div>
          <div class="bc-section__sub">All booking times will be interpreted in this timezone.</div>
        </div>
      </div>
      <div class="bc-section__body">
        <div class="bc-field bc-field--wide">
          <label class="bc-label">Default Timezone</label>
          <p-select appendTo="body" [filter]="true" filterPlaceholder="Search"
            [options]="timezones"
            [(ngModel)]="config.timezone"
            optionLabel="label"
            optionValue="value"
            placeholder="Select timezone"
            styleClass="bc-select">
          </p-select>
        </div>
      </div>
    </div>

    <!-- Section: Session Duration -->
    <div class="bc-section">
      <div class="bc-section__header">
        <div class="bc-section__icon bc-icon--green">
          <i class="pi pi-hourglass"></i>
        </div>
        <div>
          <div class="bc-section__title">Session Duration</div>
          <div class="bc-section__sub">Control the length of each booking slot.</div>
        </div>
      </div>
      <div class="bc-section__body">
        <div class="bc-grid bc-grid--3">
          <div class="bc-field">
            <label class="bc-label">
              Min Duration
              <span class="bc-label__unit">minutes</span>
            </label>
            <p-inputnumber
              [(ngModel)]="config.minDurationMinutes"
              [min]="5" [max]="480" [step]="5"
              styleClass="bc-inputnumber"
              placeholder="30">
            </p-inputnumber>
          </div>
          <div class="bc-field">
            <label class="bc-label">
              Max Duration
              <span class="bc-label__unit">minutes</span>
            </label>
            <p-inputnumber
              [(ngModel)]="config.maxDurationMinutes"
              [min]="5" [max]="480" [step]="5"
              styleClass="bc-inputnumber"
              placeholder="60">
            </p-inputnumber>
          </div>
          <div class="bc-field">
            <label class="bc-label">
              Step Interval
              <span class="bc-label__unit">minutes</span>
            </label>
            <p-inputnumber
              [(ngModel)]="config.stepMinutes"
              [min]="5" [max]="120" [step]="5"
              styleClass="bc-inputnumber"
              placeholder="15">
            </p-inputnumber>
          </div>
        </div>
        <div *ngIf="config.minDurationMinutes > config.maxDurationMinutes" class="bc-inline-error">
          <i class="pi pi-exclamation-triangle"></i>
          Min duration cannot exceed max duration.
        </div>
      </div>
    </div>

    <!-- Section: Timing Rules -->
    <div class="bc-section">
      <div class="bc-section__header">
        <div class="bc-section__icon bc-icon--purple">
          <i class="pi pi-stopwatch"></i>
        </div>
        <div>
          <div class="bc-section__title">Timing Rules</div>
          <div class="bc-section__sub">Define buffers and advance notice requirements.</div>
        </div>
      </div>
      <div class="bc-section__body">
        <div class="bc-grid bc-grid--3">
          <div class="bc-field">
            <label class="bc-label">
              Buffer Time
              <span class="bc-label__unit">minutes</span>
              <i class="pi pi-info-circle bc-label__info"
                 pTooltip="Gap added between consecutive bookings."
                 tooltipPosition="top"></i>
            </label>
            <p-inputnumber
              [(ngModel)]="config.bufferMinutes"
              [min]="0" [max]="120" [step]="5"
              styleClass="bc-inputnumber"
              placeholder="10">
            </p-inputnumber>
          </div>
          <div class="bc-field">
            <label class="bc-label">
              Min Notice
              <span class="bc-label__unit">hours</span>
              <i class="pi pi-info-circle bc-label__info"
                 pTooltip="Minimum hours before a booking can be made."
                 tooltipPosition="top"></i>
            </label>
            <p-inputnumber
              [(ngModel)]="config.minNoticeHours"
              [min]="0" [max]="168" [step]="1"
              styleClass="bc-inputnumber"
              placeholder="2">
            </p-inputnumber>
          </div>
          <div class="bc-field">
            <label class="bc-label">
              Cancellation Cutoff
              <span class="bc-label__unit">hours</span>
              <i class="pi pi-info-circle bc-label__info"
                 pTooltip="Bookings cannot be cancelled after this many hours before the appointment."
                 tooltipPosition="top"></i>
            </label>
            <p-inputnumber
              [(ngModel)]="config.cancellationCutoffHours"
              [min]="0" [max]="168" [step]="1"
              styleClass="bc-inputnumber"
              placeholder="4">
            </p-inputnumber>
          </div>
        </div>
      </div>
    </div>

    <!-- Section: Booking Limits -->
    <div class="bc-section">
      <div class="bc-section__header">
        <div class="bc-section__icon bc-icon--orange">
          <i class="pi pi-chart-bar"></i>
        </div>
        <div>
          <div class="bc-section__title">Booking Window & Limits</div>
          <div class="bc-section__sub">Control how far ahead users can book and daily maximums.</div>
        </div>
      </div>
      <div class="bc-section__body">
        <div class="bc-grid bc-grid--2">
          <div class="bc-field">
            <label class="bc-label">
              Advance Booking Window
              <span class="bc-label__unit">days</span>
              <i class="pi pi-info-circle bc-label__info"
                 pTooltip="How many days in the future a booking can be made."
                 tooltipPosition="top"></i>
            </label>
            <p-inputnumber
              [(ngModel)]="config.advanceBookingDays"
              [min]="1" [max]="365" [step]="1"
              styleClass="bc-inputnumber"
              placeholder="14">
            </p-inputnumber>
          </div>
          <div class="bc-field">
            <label class="bc-label">
              Max Bookings Per Day
              <i class="pi pi-info-circle bc-label__info"
                 pTooltip="Leave blank for unlimited bookings per day."
                 tooltipPosition="top"></i>
            </label>
            <p-inputnumber
              [(ngModel)]="config.maxBookingsPerDay"
              [min]="1" [max]="9999"
              styleClass="bc-inputnumber"
              placeholder="Unlimited">
            </p-inputnumber>
          </div>
        </div>
      </div>
    </div>

    <!-- Section: Reschedule Policy -->
    <div class="bc-section">
      <div class="bc-section__header">
        <div class="bc-section__icon bc-icon--teal">
          <i class="pi pi-replay"></i>
        </div>
        <div>
          <div class="bc-section__title">Reschedule Policy</div>
          <div class="bc-section__sub">Allow or prevent customers from rescheduling their appointments.</div>
        </div>
      </div>
      <div class="bc-section__body">
        <div class="bc-toggle-row">
          <div>
            <div class="bc-toggle-label">Allow Reschedule</div>
            <div class="bc-toggle-sub">When enabled, customers can reschedule booked appointments.</div>
          </div>
          <p-toggleswitch [(ngModel)]="config.allowReschedule"></p-toggleswitch>
        </div>
      </div>
    </div>

    <!-- Save Bar -->
    <div class="bc-save-bar">
      <p-button
        label="Save Configuration"
        icon="pi pi-check"
        [loading]="savingConfig"
        [disabled]="config.minDurationMinutes > config.maxDurationMinutes"
        (onClick)="saveConfig()"
        styleClass="p-button-sm bc-save-btn">
      </p-button>
    </div>

  </div>
  <!-- /general tab -->

  <!-- ══════════════ WORKING HOURS TAB ══════════════════════ -->
  <div *ngIf="bookingTab === 'hours'" class="bc-tab-body">

    <div *ngIf="loadingHours" class="settings-loading">
      <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
      <span>Loading working hours…</span>
    </div>

    <div class="bc-section">
      <div class="bc-section__header">
        <div class="bc-section__icon bc-icon--green">
          <i class="pi pi-calendar-clock"></i>
        </div>
        <div>
          <div class="bc-section__title">Weekly Schedule</div>
          <div class="bc-section__sub">Set the days and hours your business is open for appointments.</div>
        </div>
      </div>

      <!-- Schedule Grid -->
      <div class="bc-schedule">

        <div class="bc-schedule__header">
          <div class="bc-schedule__col bc-schedule__col--day">Day</div>
          <div class="bc-schedule__col bc-schedule__col--toggle">Open</div>
          <div class="bc-schedule__col bc-schedule__col--time">Start Time</div>
          <div class="bc-schedule__col bc-schedule__col--time">End Time</div>
          <div class="bc-schedule__col bc-schedule__col--status">Status</div>
        </div>

        <div class="bc-schedule__body">
          <div
            *ngFor="let day of schedule"
            class="bc-schedule__row"
            [class.bc-schedule__row--closed]="!day.isWorkingDay">

            <!-- Day label -->
            <div class="bc-schedule__col bc-schedule__col--day">
              <span class="bc-day-badge" [class.bc-day-badge--active]="day.isWorkingDay">
                {{ day.dayOfWeek }}
              </span>
              <span class="bc-day-name">{{ dayLabels[day.dayOfWeek] }}</span>
            </div>

            <!-- Toggle -->
            <div class="bc-schedule__col bc-schedule__col--toggle">
              <p-toggleswitch
                [(ngModel)]="day.isWorkingDay"
                (onChange)="onDayToggle(day)">
              </p-toggleswitch>
            </div>

            <!-- Start Time -->
            <div class="bc-schedule__col bc-schedule__col--time">
              <input
                type="time"
                class="bc-time-input"
                [class.bc-time-input--disabled]="!day.isWorkingDay"
                [(ngModel)]="day.startTime"
                [disabled]="!day.isWorkingDay"
                placeholder="09:00" />
            </div>

            <!-- End Time -->
            <div class="bc-schedule__col bc-schedule__col--time">
              <input
                type="time"
                class="bc-time-input"
                [class.bc-time-input--disabled]="!day.isWorkingDay"
                [(ngModel)]="day.endTime"
                [disabled]="!day.isWorkingDay"
                placeholder="18:00" />
            </div>

            <!-- Status pill -->
            <div class="bc-schedule__col bc-schedule__col--status">
              <span class="bc-status-pill" [class.bc-status-pill--open]="day.isWorkingDay">
                {{ day.isWorkingDay ? 'Open' : 'Closed' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Save Bar -->
    <div class="bc-save-bar">
      <p-button
        label="Save Working Hours"
        icon="pi pi-check"
        [loading]="savingHours"
        (onClick)="saveWorkingHours()"
        styleClass="p-button-sm bc-save-btn">
      </p-button>
    </div>

  </div>
  <!-- /hours tab -->

  <!-- ══════════════ BLOCKED DATES TAB ══════════════════════ -->
  <div *ngIf="bookingTab === 'blocked'" class="bc-tab-body">

    <!-- Blocked Dates header bar -->
    <div class="bc-table-header">
      <div class="bc-table-header__left">
        <div class="bc-section__icon bc-icon--red">
          <i class="pi pi-ban"></i>
        </div>
        <div>
          <div class="bc-section__title">Blocked Dates</div>
          <div class="bc-section__sub">Holidays, closures, and leave days with no bookings allowed.</div>
        </div>
      </div>
      <p-button
        label="Add Blocked Date"
        icon="pi pi-plus"
        (onClick)="openAddBlockedDialog()"
        styleClass="p-button-sm">
      </p-button>
    </div>

    <div *ngIf="loadingBlocked" class="settings-loading">
      <p-progressSpinner strokeWidth="4" styleClass="settings-spinner"></p-progressSpinner>
      <span>Loading blocked dates…</span>
    </div>

    <!-- Empty state -->
    <div *ngIf="!loadingBlocked && blockedDates.length === 0" class="bc-empty">
      <div class="bc-empty__icon"><i class="pi pi-calendar-times"></i></div>
      <div class="bc-empty__title">No blocked dates yet</div>
      <div class="bc-empty__sub">Add holidays, closures, or leave days to prevent bookings on those dates.</div>
      <p-button label="Add First Blocked Date" icon="pi pi-plus" (onClick)="openAddBlockedDialog()" styleClass="p-button-sm p-button-outlined mt-3"></p-button>
    </div>

    <!-- Blocked dates table -->
    <div *ngIf="!loadingBlocked && blockedDates.length > 0" class="settings-card bc-table-card">
      <p-table
        [value]="blockedDates"
        [paginator]="blockedDates.length > 10"
        [rows]="10"
        styleClass="bc-table"
        [sortField]="'date'"
        [sortOrder]="1"
        responsiveLayout="scroll">

        <ng-template pTemplate="header">
          <tr>
            <th pSortableColumn="date" style="width: 160px">
              Date <p-sortIcon field="date"></p-sortIcon>
            </th>
            <th pSortableColumn="type" style="width: 140px">
              Type <p-sortIcon field="type"></p-sortIcon>
            </th>
            <th>Reason</th>
            <th style="width: 110px; text-align:center">Recurring</th>
            <th style="width: 90px; text-align:center">Actions</th>
          </tr>
        </ng-template>

        <ng-template pTemplate="body" let-row>
          <tr>
            <td>
              <div class="bc-date-cell">
                <i class="pi pi-calendar bc-date-cell__icon"></i>
                <span>{{ row.date }}</span>
              </div>
            </td>
            <td>
              <p-tag
                [value]="getTypeLabel(row.type)"
                [severity]="getTypeSeverity(row.type)"
                styleClass="bc-type-tag">
              </p-tag>
            </td>
            <td>
              <span class="bc-reason-text">{{ row.reason }}</span>
            </td>
            <td style="text-align:center">
              <i
                class="pi"
                [class.pi-sync]="row.isRecurring"
                [class.pi-minus]="!row.isRecurring"
                [pTooltip]="row.isRecurring ? 'Repeats annually' : 'One-time'"
                tooltipPosition="top"
                [style.color]="row.isRecurring ? '#16a34a' : '#94a3b8'"
                style="font-size: 1rem">
              </i>
            </td>
            <td style="text-align:center">
              <p-button
                icon="pi pi-trash"
                severity="danger"
                [text]="true"
                [rounded]="true"
                size="small"
                [loading]="deletingBlockedId === row.id"
                (onClick)="deleteBlockedDate(row.id)"
                pTooltip="Remove blocked date"
                tooltipPosition="left">
              </p-button>
            </td>
          </tr>
        </ng-template>

      </p-table>
    </div>

  </div>
  <!-- /blocked tab -->

</div>

<!-- ══════════════ ADD BLOCKED DATE DIALOG ════════════════ -->
<p-dialog
  header="Add Blocked Date"
  [(visible)]="showAddBlockedDialog"
  [modal]="true"
  [closable]="true"
  [draggable]="false"
  [resizable]="false"
  styleClass="bc-dialog"
  [style]="{ width: '460px' }">

  <div class="bc-dialog-body">

    <!-- Date picker -->
    <div class="bc-field">
      <label class="bc-label">Date <span class="bc-required">*</span></label>
      <p-datepicker appendTo="body"
        [(ngModel)]="blockedForm.date"
        dateFormat="yy-mm-dd"
        [showIcon]="true"
        [minDate]="today"
        styleClass="bc-datepicker"
        placeholder="Select date">
      </p-datepicker>
    </div>

    <!-- Type -->
    <div class="bc-field">
      <label class="bc-label">Type <span class="bc-required">*</span></label>
      <p-select appendTo="body" [filter]="true" filterPlaceholder="Search"
        [options]="blockedDateTypes"
        [(ngModel)]="blockedForm.type"
        optionLabel="label"
        optionValue="value"
        styleClass="bc-select">
      </p-select>
    </div>

    <!-- Reason -->
    <div class="bc-field">
      <label class="bc-label">Reason <span class="bc-required">*</span></label>
      <textarea
        pTextarea
        [(ngModel)]="blockedForm.reason"
        rows="3"
        placeholder="e.g. Independence Day"
        class="bc-textarea">
      </textarea>
    </div>

    <!-- Recurring toggle -->
    <div class="bc-toggle-row bc-toggle-row--compact">
      <div>
        <div class="bc-toggle-label">Recurring annually</div>
        <div class="bc-toggle-sub">This date will be blocked every year.</div>
      </div>
      <p-toggleswitch [(ngModel)]="blockedForm.isRecurring"></p-toggleswitch>
    </div>

  </div>

  <ng-template pTemplate="footer">
    <p-button label="Cancel" [text]="true" (onClick)="showAddBlockedDialog = false" styleClass="p-button-sm"></p-button>
    <p-button
      label="Add Blocked Date"
      icon="pi pi-check"
      [loading]="savingBlocked"
      (onClick)="addBlockedDate()"
      styleClass="p-button-sm">
    </p-button>
  </ng-template>

</p-dialog>
